import { forwardRef, useCallback, useState } from 'react'
import { t } from '@lingui/core/macro'
import { Button } from '@components/v2/button'
import { cn } from '@global/utils/tailwind'
import AmazingEmoji from 'images/amazing-emoji.svg'
import GoodEmoji from 'images/good-emoji.svg'
import NotGoodEmoji from 'images/not-good-emoji.svg'
import OkayEmoji from 'images/okay-emoji.svg'
import TerribleEmoji from 'images/terrible-emoji.svg'
import { Modal, ModalInterface } from 'components'
import { FeedbackSubmissionRequest } from '@taxonomy-translation/types'

interface FeedbackValues {
  rating: number | string
  comment: string
  acceptContact: boolean
}

interface FeedbackProps {
  onSubmit: (values: FeedbackSubmissionRequest) => Promise<void> | void
  subtitle: string
}

const getFeedbackOptions = () => [
  { emoji: TerribleEmoji, text: t`Terrible`, value: '1' },
  { emoji: NotGoodEmoji, text: t`Not good`, value: '2' },
  { emoji: OkayEmoji, text: t`Okay`, value: '3' },
  { emoji: GoodEmoji, text: t`Good`, value: '4' },
  { emoji: AmazingEmoji, text: t`Amazing`, value: '5' },
]

export const Feedback = forwardRef<ModalInterface, FeedbackProps>(({ onSubmit, subtitle }, ref) => {
  const [formValues, setFormValues] = useState<FeedbackValues>({
    rating: '0',
    comment: '',
    acceptContact: false,
  })

  const handleClose = () => {
    if (ref && 'current' in ref && ref.current) {
      ref.current.handleCloseModal()
    }
  }

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      const data = {
        rating: Number(formValues.rating),
        comment: formValues.comment,
        acceptContact: formValues.acceptContact,
      }

      e.preventDefault()
      await onSubmit(data)
      handleClose()
    },
    [formValues, onSubmit],
  )

  const handleFormValues = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = event.target

      setFormValues(prevState => ({
        ...prevState,
        [name]: value,
      }))
    },
    [],
  )

  return (
    <Modal ref={ref}>
      <div>
        <h2 className="text-xl font-bold mb-9">{t`Give us your feedback`}</h2>
        <p className="text-gray-700 mb-4 text-cm">{subtitle}</p>
        <form onSubmit={(...args) => void handleSubmit(...args)}>
          <div className="flex mb-9">
            {getFeedbackOptions().map(({ emoji: Emoji, text, value }) => (
              <label
                key={value}
                className={cn('flex flex-col items-center cursor-pointer mr-4', {
                  'font-bold': formValues.rating === value,
                  'hover:font-bold': formValues.rating !== value,
                })}
              >
                <input
                  type="radio"
                  name="rating"
                  value={value}
                  checked={formValues.rating === value}
                  onChange={handleFormValues}
                  className="hidden"
                />
                <div
                  className={cn(
                    `w-[54px] h-[54px]
                    flex items-center justify-center
                    rounded-md border-2
                    bg-cv-slate-50`,
                    formValues.rating === value && 'border-cv-alice-blue-500 bg-cv-slate-150',
                    formValues.rating !== value &&
                      'border-transparent hover:border-cv-alice-blue-500 hover:bg-cv-slate-150',
                  )}
                >
                  <Emoji />
                </div>
                <span className="text-center mt-2 text-sm">{text}</span>
              </label>
            ))}
          </div>
          <p className="text-gray-700 text-sm mb-2">
            {t`Any thoughts you would like to share?`}
            <span className="text-[#A9C5D0]">{t`(Optional)`}</span>
          </p>
          <textarea
            name="comment"
            className="w-full border p-2 rounded-md resize-none mb-9"
            placeholder={t`Leave a comment...`}
            value={formValues.comment}
            onChange={handleFormValues}
          />
          <div className="flex mb-12">
            <input
              type="checkbox"
              id="acceptContact"
              name="contactMy"
              value="true"
              className="mr-2.5"
              onChange={handleFormValues}
            />
            <label htmlFor="acceptContact" className="text-sm">
              {t`I would like to be contacted for future feedback opportunities`}
            </label>
          </div>
          <div className="flex justify-between mt-4">
            <Button onClick={handleClose} variant="outline" className="bg-cv-slate-100">
              {t`Close`}
            </Button>
            <Button type="submit" disabled={!Number(formValues.rating)}>
              {t`Submit`}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
})

Feedback.displayName = 'Feedback'
